"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { FootballScoreboard } from "@/components/site/football-scoreboard";
import { EDITORIAL_LABELS } from "@/lib/editorial-labels";
import type { FootballLeagueStandings, FootballScoreboardFreshness, FootballScoreboardMatch } from "@/lib/football-scoreboard-data";
import type { HeaderLeague, HeaderTeam } from "@/lib/league-data";

const WORLD_CUP_OFFICIAL_EMBLEM_SRC = "https://crests.football-data.org/WC.png";
const WORLD_CUP_NAV_ICON_FALLBACK_SRC = "/league-logos/worldcup_minilogo.png";

function isWorldCupNavItem(league: HeaderLeague) {
  const label = league.name.toLocaleLowerCase("hu-HU");

  return (
    label === "világbajnokság" ||
    label.includes("world cup") ||
    league.slug === "world-cup" ||
    league.href === "/world-cup"
  );
}

function LeagueLogo({ league }: { league: HeaderLeague }) {
  const isWorldCup = isWorldCupNavItem(league);
  const iconUrl = isWorldCup ? (league.hasOfficialLogo ? league.iconUrl : WORLD_CUP_OFFICIAL_EMBLEM_SRC) : league.iconUrl;

  if (!iconUrl) return null;

  if (isWorldCup) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={iconUrl}
        alt=""
        className="h-7 w-7 shrink-0 object-contain"
        loading="lazy"
        decoding="async"
        onError={(event) => {
          event.currentTarget.src = WORLD_CUP_NAV_ICON_FALLBACK_SRC;
        }}
      />
    );
  }

  return (
    <span className="grid h-7 w-7 shrink-0 place-items-center rounded-sm border border-black/[0.04] bg-white/70">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={iconUrl}
        alt=""
        className="h-6 w-6 object-contain"
        loading="lazy"
        decoding="async"
      />
    </span>
  );
}

function TeamCrest({ src }: { src?: string | null }) {
  if (!src) return <span className="h-2 w-2 rounded-full bg-[#D8973C]" />;

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img src={src} alt="" className="h-4 w-4 object-contain" loading="lazy" decoding="async" />
  );
}

export function FootballContextNav({
  leagues,
  activeLeagueSlug,
  activeTeamSlug,
  matches,
  standings,
  homepageStandingsByLeague,
  scoreboardFreshness,
  canRefreshScoreboard = false,
}: {
  leagues: HeaderLeague[];
  activeLeagueSlug?: string;
  activeTeamSlug?: string;
  matches: FootballScoreboardMatch[];
  standings: FootballLeagueStandings | null;
  homepageStandingsByLeague?: Record<string, FootballLeagueStandings | null>;
  scoreboardFreshness?: FootballScoreboardFreshness | null;
  canRefreshScoreboard?: boolean;
}) {
  const leagueNavRef = useRef<HTMLElement | null>(null);
  const teamNavRef = useRef<HTMLElement | null>(null);
  const [canScrollLeagueLeft, setCanScrollLeagueLeft] = useState(false);
  const [canScrollLeagueRight, setCanScrollLeagueRight] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const activeLeague = activeLeagueSlug
    ? leagues.find((league) => league.slug === activeLeagueSlug || (activeLeagueSlug === "world-cup" && isWorldCupNavItem(league))) ?? null
    : null;
  const scoreboardActiveLeagueSlug = activeLeagueSlug ?? activeLeague?.slug;

  useEffect(() => {
    const element = leagueNavRef.current;
    if (!element) return;

    const updateScrollState = () => {
      setCanScrollLeagueLeft(element.scrollLeft > 4);
      setCanScrollLeagueRight(element.scrollLeft + element.clientWidth < element.scrollWidth - 4);
    };

    updateScrollState();
    element.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);

    return () => {
      element.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [leagues]);

  useEffect(() => {
    if (!activeLeague) return;

    const element = teamNavRef.current;
    if (!element) return;

    const updateScrollState = () => {
      setCanScrollLeft(element.scrollLeft > 4);
      setCanScrollRight(element.scrollLeft + element.clientWidth < element.scrollWidth - 4);
    };

    updateScrollState();
    element.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);

    return () => {
      element.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [activeLeague]);

  function scrollTeams(direction: "left" | "right") {
    const element = teamNavRef.current;
    if (!element) return;

    element.scrollBy({
      left: direction === "left" ? -240 : 240,
      behavior: "smooth",
    });
  }

  function scrollLeagues(direction: "left" | "right") {
    const element = leagueNavRef.current;
    if (!element) return;

    element.scrollBy({
      left: direction === "left" ? -220 : 220,
      behavior: "smooth",
    });
  }

  return (
    <section aria-label="Football navigáció" className="border-t border-black/[0.08] bg-white">
      <div className="relative mx-auto flex min-h-10 max-w-7xl items-center justify-center pl-0 pr-8 py-1 sm:px-6">
        <button
          type="button"
          aria-label="Előző ligák"
          disabled={!canScrollLeagueLeft}
          className="absolute left-1 top-1/2 z-10 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-sm border border-black/[0.08] bg-white/90 text-[#273E47] shadow-sm transition hover:bg-[#F3F0E8] disabled:pointer-events-none disabled:opacity-0 sm:hidden"
          onClick={() => scrollLeagues("left")}
        >
          <ChevronLeft size={16} aria-hidden="true" />
        </button>
        <nav
          ref={leagueNavRef}
          aria-label="Football ligák"
          className="flex min-w-0 flex-1 flex-nowrap items-center justify-start gap-1.5 overflow-x-auto whitespace-nowrap [scrollbar-width:none] sm:flex-none sm:flex-wrap sm:justify-center sm:gap-2 [&::-webkit-scrollbar]:hidden"
        >
          {leagues.map((league) => {
            const active = league.slug === activeLeagueSlug || (activeLeagueSlug === "world-cup" && isWorldCupNavItem(league));

            return (
              <Link
                key={league.slug}
                href={league.href}
                aria-current={active ? "page" : undefined}
                className={`relative inline-flex h-10 shrink-0 items-center ${isWorldCupNavItem(league) ? "gap-1 sm:gap-1.5" : "gap-1.5 sm:gap-2"} rounded-sm px-2.5 text-sm font-semibold sm:px-3.5 ${
                  active
                    ? "bg-[#F7F1EC] text-[#A4243B]"
                    : "bg-transparent text-[#273E47] transition hover:bg-[#F3F0E8] active:bg-[#ECE4DA]"
                }`}
              >
                <LeagueLogo league={league} />
                {league.name}
              </Link>
            );
          })}
          <span aria-hidden="true" className="mx-3 h-6 w-px shrink-0 bg-black/[0.14]" />
          <Link
            href="/transfers"
            className="inline-flex h-10 shrink-0 items-center text-[13px] font-medium text-[#273E47]/65 underline-offset-4 transition hover:text-[#A4243B] hover:underline"
          >
            {EDITORIAL_LABELS.homepage.transferNews}
          </Link>
        </nav>
        <button
          type="button"
          aria-label="Következő ligák"
          disabled={!canScrollLeagueRight}
          className="absolute right-1 top-1/2 z-10 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-sm border border-black/[0.08] bg-white/90 text-[#273E47] shadow-sm transition hover:bg-[#F3F0E8] disabled:pointer-events-none disabled:opacity-0 sm:hidden"
          onClick={() => scrollLeagues("right")}
        >
          <ChevronRight size={16} aria-hidden="true" />
        </button>
      </div>

      {activeLeague ? (
        <>
          {activeLeague.teams.length ? (
            <div className="border-t border-[#D8973C] bg-[#273E47]">
              <div className="mx-auto flex h-14 max-w-7xl items-center gap-2 px-3 sm:px-6">
                <button
                  type="button"
                  aria-label="Előző csapatok"
                  disabled={!canScrollLeft}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-white/20 text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-35"
                  onClick={() => scrollTeams("left")}
                >
                  <ChevronLeft size={18} aria-hidden="true" />
                </button>
                <nav
                  ref={teamNavRef}
                  aria-label={`${activeLeague.name} csapatok`}
                  className="flex min-w-0 flex-1 gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                >
                  {activeLeague.teams.map((team: HeaderTeam) => {
                    const active = team.slug === activeTeamSlug;

                    return (
                      <Link
                        key={team.slug}
                        href={team.href}
                        title={team.name}
                        aria-current={active ? "page" : undefined}
                        className={`inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md px-2.5 text-sm font-semibold transition ${
                          active
                            ? "bg-white text-[#A4243B]"
                            : "text-white/85 hover:bg-white/10 hover:text-white"
                        }`}
                      >
                        <TeamCrest src={team.crest ?? team.logo} />
                        <span className="max-w-[7rem] truncate sm:max-w-none">{team.name}</span>
                      </Link>
                    );
                  })}
                </nav>
                <button
                  type="button"
                  aria-label="Következő csapatok"
                  disabled={!canScrollRight}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-white/20 text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-35"
                  onClick={() => scrollTeams("right")}
                >
                  <ChevronRight size={18} aria-hidden="true" />
                </button>
              </div>
            </div>
          ) : null}
          <div className="border-b border-black/[0.08] bg-white">
            <div className="mx-auto max-w-7xl px-0 sm:px-6">
              <FootballScoreboard
                key={scoreboardActiveLeagueSlug}
                matches={matches}
                activeLeagueSlug={scoreboardActiveLeagueSlug}
                activeTeamSlug={activeTeamSlug}
                standings={standings}
                freshness={scoreboardFreshness}
                canRefresh={canRefreshScoreboard}
              />
            </div>
          </div>
        </>
      ) : homepageStandingsByLeague ? (
        <div className="border-b border-black/[0.08] bg-white">
          <div className="mx-auto max-w-7xl px-0 sm:px-6">
            <FootballScoreboard
              matches={matches}
              standingsLeagueOptions={leagues.map((league) => ({
                slug: league.slug,
                name: league.name,
              }))}
              standingsByLeague={homepageStandingsByLeague}
              freshness={scoreboardFreshness}
              canRefresh={canRefreshScoreboard}
            />
          </div>
        </div>
      ) : null}
    </section>
  );
}
